import React, { forwardRef, useImperativeHandle } from "react";
import { Box, Typography } from "@mui/material";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import {
  pdfSliceAndSave,
  injectPageBreakSpacers,
  removePageBreakSpacers,
} from "./pdfUtils";

const SECTION_COLORS = ["#ED1C2E", "#263842", "#2E7D32", "#009CCC", "#FF9800"];

const computePersonalAverages = (responses) => {
  const sectionMap = {};
  const sectionOrder = [];
  responses.forEach((r) => {
    const sectionName = r.section?.name;
    if (!sectionName || !r.reflection?.self_rating) return;
    if (!sectionMap[sectionName]) {
      sectionMap[sectionName] = { ratings: [] };
      sectionOrder.push(sectionName);
    }
    sectionMap[sectionName].ratings.push(Number(r.reflection.self_rating));
  });
  return sectionOrder.map((name, idx) => {
    const { ratings } = sectionMap[name];
    const score = parseFloat(
      (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1),
    );
    return { name, score, color: SECTION_COLORS[idx] || "#263842" };
  });
};

const computePersonalSubsectionAverages = (responses) => {
  const subMap = {};
  const subOrder = [];
  responses.forEach((r) => {
    const subName = r.subsection?.name;
    const sectionName = r.section?.name;
    if (!subName || !r.reflection?.self_rating) return;
    if (!subMap[subName]) {
      subMap[subName] = { ratings: [], section: sectionName };
      subOrder.push(subName);
    }
    subMap[subName].ratings.push(Number(r.reflection.self_rating));
  });
  return subOrder.map((name) => {
    const { ratings, section } = subMap[name];
    const score = parseFloat(
      (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(1),
    );
    return { name, score, section };
  });
};

const PersonalHealthReportPdf = forwardRef(
  (
    {
      reflectionResponses = [],
      sections = [],
      userName,
      font = "Gotham, sans-serif",
    },
    ref,
  ) => {
    const personalSections = computePersonalAverages(reflectionResponses);
    const personalSubsections =
      computePersonalSubsectionAverages(reflectionResponses);
    const overallPersonalScore =
      personalSections.length > 0
        ? (
            personalSections.reduce((a, s) => a + s.score, 0) /
            personalSections.length
          ).toFixed(1)
        : null;

    const drawScoreBadge = (scoreText) => {
      const c = document.createElement("canvas");
      c.width = 200;
      c.height = 100;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#FFCC32";
      ctx.beginPath();
      ctx.roundRect(0, 0, 200, 100, 10);
      ctx.fill();
      ctx.fillStyle = "#263842";
      ctx.font = "bold 54px Arial, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "alphabetic";
      ctx.fillText(scoreText, 100, 62);
      ctx.fillStyle = "#50545D";
      ctx.font = "18px Arial, sans-serif";
      ctx.fillText("out of 5.0", 100, 86);
      return c.toDataURL("image/png");
    };

    useImperativeHandle(ref, () => ({
      download: async () => {
        const badgeEl = document.getElementById("pdf-personal-score-badge-img");
        if (badgeEl && overallPersonalScore) {
          badgeEl.src = drawScoreBadge(overallPersonalScore);
          badgeEl.style.display = "block";
        }

        const el = document.getElementById("personal-health-report-content");
        if (!el) return;

        const SCALE = 1.5;
        const margins = { top: 20, bottom: 20, left: 20, right: 20 };

        // Calculate one A4 page height in DOM pixels at this scale
        const A4_WIDTH_MM = 210;
        const A4_HEIGHT_MM = 297;
        const imgWidthMm = A4_WIDTH_MM - margins.left - margins.right; // 170 mm
        const canvasWidthPx = el.offsetWidth * SCALE;
        const maxPageHeightCanvas =
          ((A4_HEIGHT_MM - margins.top - margins.bottom) * canvasWidthPx) /
          imgWidthMm;
        const pageHeightDomPx = maxPageHeightCanvas / SCALE;

        // Push any block that straddles a page boundary onto the next page
        injectPageBreakSpacers(el, pageHeightDomPx);

        // Let the browser reflow the inserted spacers before capture
        await new Promise((resolve) => requestAnimationFrame(resolve));

        const canvas = await html2canvas(el, {
          scale: SCALE,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
        });

        // Restore DOM — spacers are no longer needed after the canvas is captured
        removePageBreakSpacers(el);

        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "mm",
          format: "a4",
        });
        pdfSliceAndSave(
          canvas,
          pdf,
          margins,
          "GCCHF_Personal_Health_Report.pdf",
        );
      },
    }));

    return (
      <Box
        id="personal-health-report-content"
        sx={{
          position: "fixed",
          top: 0,
          left: "200vw",
          zIndex: -9999,
          width: 1100,
          overflow: "hidden",
          bgcolor: "#ffffff",
          fontFamily: font,
        }}
      >
        {/* Cover Header */}
        <Box sx={{ bgcolor: "#263842", px: 4, py: 3.5 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {/* LEFT SECTION */}
            <Box>
              <Typography
                sx={{
                  color: "#009CCC",
                  fontSize: 18,
                  fontWeight: 700,
                  fontFamily: font,
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  mb: 0.5,
                }}
              >
                City to City Asia Pacific
              </Typography>

              <Typography
                sx={{
                  color: "#fff",
                  fontSize: 32,
                  fontWeight: 700,
                  fontFamily: font,
                  lineHeight: 1.2,
                }}
              >
                Personal The Gospel and Church Health Framework Health Report
              </Typography>

              {/* ✅ FIXED ROW ALIGNMENT */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center", // ensures same baseline
                  gap: 1,
                  mt: 1,
                }}
              >
                <Typography
                  sx={{
                    color: "#9AA4AF",
                    fontSize: 18,
                    fontFamily: font,
                  }}
                >
                  {userName ? userName : "Leader Self-Reflection"}
                </Typography>

                {/* Separator */}
                <Typography
                  sx={{
                    color: "#9AA4AF",
                    fontSize: 18,
                    fontFamily: font,
                  }}
                >
                  |
                </Typography>

                <Typography
                  sx={{
                    color: "#9AA4AF",
                    fontSize: 18,
                    fontFamily: font,
                  }}
                >
                  {new Date().toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </Typography>
              </Box>
            </Box>

            {/* RIGHT SECTION */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                mt: 2,
              }}
            >
              <img
                id="pdf-personal-score-badge-img"
                alt="Personal Score"
                style={{ display: "none", width: 150, height: "auto" }}
              />

              <Typography
                sx={{
                  color: "#9AA4AF",
                  fontSize: 13,
                  fontWeight: 700,
                  fontFamily: font,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                  mt: 0.5,
                }}
              >
                Personal Score
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* About this report */}
        <Box
          sx={{
            bgcolor: "#f8fafc",
            borderBottom: "1px solid #C4C4C4",
            px: 4,
            py: 1.5,
          }}
        >
          <Typography
            sx={{
              fontFamily: font,
              fontSize: 17,
              color: "#50545D",
              lineHeight: 1.7,
            }}
          >
            This report reflects your personal self-ratings and written
            reflections submitted during the The Gospel and Church Health
            Framework. It is intended to support your individual leadership
            development journey alongside the broader church health results.
          </Typography>
        </Box>

        <Box sx={{ px: 4, py: 3 }}>
          {/* Personal Section Averages */}
          {personalSections.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box data-pdf-block="">
                <Typography
                  sx={{
                    fontFamily: font,
                    fontSize: 20,
                    fontWeight: 700,
                    color: "#263842",
                    mb: 0.4,
                    borderLeft: "3px solid #ED1C2E",
                    pl: 1.5,
                  }}
                >
                  Personal Leadership Scores by Section
                </Typography>
                <Typography
                  sx={{
                    fontFamily: font,
                    fontSize: 17,
                    color: "#9AA4AF",
                    mb: 2,
                  }}
                >
                  Based on your self-ratings across all reflection questions.
                </Typography>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.8 }}>
                {personalSections.map((s) => (
                  <Box key={s.name} data-pdf-block="">
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 0.4,
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.8 }}
                      >
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: "50%",
                            bgcolor: s.color,
                            flexShrink: 0,
                          }}
                        />
                        <Typography
                          sx={{
                            fontFamily: font,
                            fontSize: 18,
                            fontWeight: 700,
                            color: "#50545D",
                          }}
                        >
                          {s.name}
                        </Typography>
                      </Box>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Box
                          sx={{
                            bgcolor:
                              s.score >= 4
                                ? "#eaeeea"
                                : s.score >= 3
                                  ? "#f3eedc"
                                  : "#eeebeb",
                            px: 0.8,
                            py: 0.2,
                            borderRadius: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: font,
                              fontSize: 15,
                              fontWeight: 700,
                              color:
                                s.score >= 4
                                  ? "#3a5c3a"
                                  : s.score >= 3
                                    ? "#6b500f"
                                    : "#6b2a2a",
                            }}
                          >
                            {s.score >= 4
                              ? "Strong"
                              : s.score >= 3
                                ? "Growing"
                                : "Focus Area"}
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontFamily: font,
                            fontSize: 18,
                            fontWeight: 700,
                            color: "#50545D",
                          }}
                        >
                          {s.score} / 5
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        height: 7,
                        bgcolor: "#C4C4C4",
                        borderRadius: 4,
                        overflow: "hidden",
                        mb: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          width: `${(s.score / 5) * 100}%`,
                          bgcolor: s.color,
                          borderRadius: 4,
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Subsection Self-Rating Breakdown */}
          {personalSubsections.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ borderTop: "1px solid #C4C4C4", mb: 3 }} />
              <Box data-pdf-block="">
                <Typography
                  sx={{
                    fontFamily: font,
                    fontSize: 20,
                    fontWeight: 700,
                    color: "#263842",
                    mb: 0.4,
                    borderLeft: "3px solid #ED1C2E",
                    pl: 1.5,
                  }}
                >
                  Subsection Self-Rating Breakdown
                </Typography>
                <Typography
                  sx={{
                    fontFamily: font,
                    fontSize: 17,
                    color: "#9AA4AF",
                    mb: 2,
                  }}
                >
                  Your average self-rating for each assessed subsection.
                </Typography>
              </Box>
              <Box
                sx={{
                  border: "1px solid #C4C4C4",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                {personalSubsections.map((sub, i) => (
                  <Box
                    key={sub.name}
                    data-pdf-block=""
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      px: 2,
                      py: 1.2,
                      borderTop: i === 0 ? "none" : "1px solid #C4C4C4",
                      bgcolor: i % 2 === 0 ? "#fff" : "#f8fafc",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: font,
                        fontSize: 16,
                        color: "#50545D",
                        flex: 1,
                      }}
                    >
                      {sub.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: font,
                        fontSize: 15,
                        color: "#9AA4AF",
                        width: 110,
                        flexShrink: 0,
                      }}
                    >
                      {sub.section}
                    </Typography>
                    <Box
                      sx={{
                        width: 80,
                        height: 5,
                        bgcolor: "#C4C4C4",
                        borderRadius: 4,
                        overflow: "hidden",
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        sx={{
                          height: "100%",
                          width: `${(sub.score / 5) * 100}%`,
                          bgcolor:
                            sub.score >= 4
                              ? "#263842"
                              : sub.score >= 3
                                ? "#009CCC"
                                : "#ED1C2E",
                          borderRadius: 4,
                        }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: font,
                        fontSize: 16,
                        fontWeight: 700,
                        color: "#263842",
                        width: 30,
                        textAlign: "right",
                        flexShrink: 0,
                      }}
                    >
                      {sub.score}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Strengths & Growth Areas */}
          {personalSections.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Box sx={{ borderTop: "1px solid #C4C4C4", mb: 3 }} />
              <Typography
                sx={{
                  fontFamily: font,
                  fontSize: 20,
                  fontWeight: 700,
                  color: "#263842",
                  mb: 2,
                  borderLeft: "3px solid #ED1C2E",
                  pl: 1.5,
                }}
              >
                Your Personal Strengths & Growth Areas
              </Typography>
              <Box data-pdf-block="" sx={{ display: "flex", gap: 2 }}>
                {/* Strengths */}
                <Box
                  sx={{
                    flex: 1,
                    bgcolor: "#eaeeea",
                    border: "1px solid #c4cec4",
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: font,
                      fontSize: 16,
                      fontWeight: 700,
                      color: "#3a5c3a",
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      mb: 1,
                    }}
                  >
                    Personal Strengths
                  </Typography>
                  {personalSections.filter((s) => s.score >= 3.5).length ===
                  0 ? (
                    <Typography
                      sx={{ fontFamily: font, fontSize: 15, color: "#9AA4AF" }}
                    >
                      Continue developing across all areas.
                    </Typography>
                  ) : (
                    personalSections
                      .filter((s) => s.score >= 3.5)
                      .sort((a, b) => b.score - a.score)
                      .map((s) => (
                        <Box
                          key={s.name}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.6,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: font,
                              fontSize: 16,
                              color: "#50545D",
                            }}
                          >
                            {s.name}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: font,
                              fontSize: 16,
                              fontWeight: 700,
                              color: "#3a5c3a",
                            }}
                          >
                            {s.score}/5
                          </Typography>
                        </Box>
                      ))
                  )}
                </Box>
                {/* Growth Areas */}
                <Box
                  sx={{
                    flex: 1,
                    bgcolor: "#eeebeb",
                    border: "1px solid #cec4c4",
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: font,
                      fontSize: 16,
                      fontWeight: 700,
                      color: "#6b2a2a",
                      textTransform: "uppercase",
                      letterSpacing: 1,
                      mb: 1,
                    }}
                  >
                    Growth Opportunities
                  </Typography>
                  {personalSections.filter((s) => s.score < 3.5).length ===
                  0 ? (
                    <Typography
                      sx={{ fontFamily: font, fontSize: 15, color: "#9AA4AF" }}
                    >
                      Excellent self-Framework across all areas.
                    </Typography>
                  ) : (
                    personalSections
                      .filter((s) => s.score < 3.5)
                      .sort((a, b) => a.score - b.score)
                      .map((s) => (
                        <Box
                          key={s.name}
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 0.6,
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: font,
                              fontSize: 16,
                              color: "#50545D",
                            }}
                          >
                            {s.name}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: font,
                              fontSize: 16,
                              fontWeight: 700,
                              color: "#6b2a2a",
                            }}
                          >
                            {s.score}/5
                          </Typography>
                        </Box>
                      ))
                  )}
                </Box>
              </Box>
            </Box>
          )}

          {/* Reflection Responses */}
          <Box sx={{ borderTop: "1px solid #C4C4C4", mb: 3 }} />
          <Box data-pdf-block="">
            <Typography
              sx={{
                fontFamily: font,
                fontSize: 20,
                fontWeight: 700,
                color: "#263842",
                mb: 0.4,
                borderLeft: "3px solid #ED1C2E",
                pl: 1.5,
              }}
            >
              Written Reflection Responses
            </Typography>
            <Typography
              sx={{ fontFamily: font, fontSize: 17, color: "#9AA4AF", mb: 2 }}
            >
              Your personal reflections submitted during the Framework,
              organised by section.
            </Typography>
          </Box>

          {(() => {
            const reflectionItems = reflectionResponses.filter(
              (r) => r.reflection?.reflection_text || r.reflection?.self_rating,
            );
            if (reflectionItems.length === 0) {
              return (
                <Typography
                  sx={{ fontFamily: font, fontSize: 16, color: "#9AA4AF" }}
                >
                  No reflection responses recorded.
                </Typography>
              );
            }
            let lastSection = null;
            return reflectionItems.map((r, i) => {
              const sectionChanged = r.section?.name !== lastSection;
              lastSection = r.section?.name;
              const sectionColor =
                SECTION_COLORS[
                  sections.findIndex((s) => s.name === r.section?.name)
                ] || "#263842";
              return (
                <Box key={i} data-pdf-block="">
                  {sectionChanged && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                        mt: i === 0 ? 0 : 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "2px",
                          bgcolor: sectionColor,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: font,
                          fontSize: 17,
                          fontWeight: 700,
                          color: "#263842",
                          textTransform: "uppercase",
                          letterSpacing: 0.8,
                        }}
                      >
                        {r.section?.name}
                      </Typography>
                    </Box>
                  )}
                  <Box
                    sx={{
                      bgcolor: "#f8fafc",
                      border: "1px solid #C4C4C4",
                      borderRadius: 1.5,
                      p: 1.8,
                      mb: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        mb: r.reflection?.reflection_text ? 1 : 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: font,
                          fontSize: 16,
                          fontWeight: 700,
                          color: "#50545D",
                          flex: 1,
                          lineHeight: 1.4,
                          pr: 1,
                        }}
                      >
                        {r.subsection?.name}
                      </Typography>
                      {r.reflection?.self_rating && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            flexShrink: 0,
                          }}
                        >
                          <Box sx={{ display: "flex", gap: 0.3 }}>
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Box
                                key={star}
                                sx={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: "50%",
                                  bgcolor:
                                    star <= r.reflection.self_rating
                                      ? "#263842"
                                      : "#C4C4C4",
                                }}
                              />
                            ))}
                          </Box>
                          <Typography
                            sx={{
                              fontFamily: font,
                              fontSize: 15,
                              fontWeight: 700,
                              color: "#263842",
                              ml: 0.3,
                            }}
                          >
                            {r.reflection.self_rating}/5
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    {r.reflection?.reflection_text && (
                      <Typography
                        sx={{
                          fontFamily: font,
                          fontSize: 16,
                          color: "#50545D",
                          lineHeight: 1.6,
                          fontStyle: "italic",
                          borderLeft: "2px solid #C4C4C4",
                          pl: 1,
                        }}
                      >
                        "{r.reflection.reflection_text}"
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            });
          })()}

          {/* Personal Development Recommendations */}
          {personalSections.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Box sx={{ borderTop: "1px solid #C4C4C4", mb: 3 }} />

              {/* ✅ Updated Title */}
              <Box data-pdf-block="">
                <Typography
                  sx={{
                    fontFamily: font,
                    fontSize: 20,
                    fontWeight: 700,
                    color: "#263842",
                    mb: 2,
                    borderLeft: "3px solid #ED1C2E",
                    pl: 1.5,
                  }}
                >
                  Personal Development Summary
                </Typography>
              </Box>

              {/* ✅ Show ALL section scores (no sorting, no slicing) */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {personalSections.map((s) => (
                  <Box key={s.name} data-pdf-block="">
                    <Typography
                      sx={{
                        fontFamily: font,
                        fontSize: 14,
                        color: "#263842",
                        lineHeight: 1.6,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ fontWeight: 700, color: "#ED1C2E" }}
                      >
                        {s.name.toUpperCase()}
                      </Box>{" "}
                      – Self Rating: <strong>{s.score} / 5</strong>
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Divider */}
              <Box sx={{ borderTop: "1px solid #C4C4C4", my: 2 }} />

              {/* ✅ 3 Static Questions */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {[
                  "What personal Gospel Rhythms would you like to start in your life?",
                  "What existing rhythms would you like to strengthen?",
                  "Are you part of a Gospel Community of pastors/leaders that you are plugged into consistently? If not, how can you plug into one?",
                ].map((q, i) => (
                  <Box key={i} data-pdf-block="">
                    <Typography
                      sx={{
                        fontFamily: font,
                        fontSize: 14,
                        color: "#263842",
                        lineHeight: 1.7,
                      }}
                    >
                      <strong>{i + 1}.</strong> {q}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            bgcolor: "#263842",
            px: 4,
            py: 2.5,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              fontFamily: font,
              fontSize: 13,
              color: "#9AA4AF",
              lineHeight: 1.4,
            }}
          >
            City to City Asia Pacific - The Gospel and Church Health Framework
          </Typography>

          <Typography
            sx={{
              fontFamily: font,
              fontSize: 13,
              color: "#009CCC",
              lineHeight: 1.4,
            }}
          >
            For coaching and next steps, contact CTCAP
          </Typography>
        </Box>
      </Box>
    );
  },
);

PersonalHealthReportPdf.displayName = "PersonalHealthReportPdf";
export default PersonalHealthReportPdf;
